import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TeeFormModal } from '@/components/ui/tee-form-modal';
import { EntityPickerCombobox } from '@/components/entity-picker/EntityPickerCombobox';
import { contentAssignmentService } from '@/services/content-assignment.service';
import { ContentItem } from '@/types/contentItem';
import { SearchResult } from '@/services/search.service';
import { showSuccessToast, showErrorToast } from '@/utils/toast.utils';

export interface AttachToPlayerModalProps {
  isOpen: boolean;
  item: ContentItem | null;
  onClose: () => void;
  onAttached?: () => void;
}

export function AttachToPlayerModal({ isOpen, item, onClose, onAttached }: AttachToPlayerModalProps) {
  const { t } = useTranslation();

  const [selected, setSelected] = useState<SearchResult | null>(null);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const requestSeqRef = useRef(0);

  const resetState = () => {
    setSelected(null);
    setIsDuplicate(false);
    setCheckingDuplicate(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSelect = async (entity: SearchResult) => {
    setSelected(entity);
    setIsDuplicate(false);

    if (!item) return;

    const seq = ++requestSeqRef.current;
    setCheckingDuplicate(true);
    try {
      const existing = await contentAssignmentService.listForPlayer(
        entity.type as 'member' | 'lead',
        entity._id,
        { contentItemId: item.id }
      );
      if (seq === requestSeqRef.current) {
        setIsDuplicate(existing.length > 0);
      }
    } catch {
      if (seq === requestSeqRef.current) {
        setIsDuplicate(false);
      }
    } finally {
      if (seq === requestSeqRef.current) {
        setCheckingDuplicate(false);
      }
    }
  };

  const handleConfirm = async () => {
    if (!item || !selected) return;

    setAttaching(true);
    try {
      await contentAssignmentService.attachToPlayer(
        selected.type as 'member' | 'lead',
        selected._id,
        { contentItemId: item.id }
      );
      showSuccessToast(t('trainingProgram.toast.attachSuccess'));
      onAttached?.();
      resetState();
      onClose();
    } catch {
      showErrorToast(t('trainingProgram.toast.attachError'));
    } finally {
      setAttaching(false);
    }
  };

  if (item === null) return null;

  return (
    <TeeFormModal
      isOpen={isOpen}
      title={t('trainingProgram.attachTargetTitle')}
      onClose={handleClose}
      primaryActionLabel={t('trainingProgram.attachTargetConfirm')}
      onPrimaryAction={handleConfirm}
      isLoading={attaching}
      primaryActionDisabled={!selected || checkingDuplicate || isDuplicate}
    >
      <div className="flex flex-col gap-4">
        <p className="text-body-s text-[var(--text-content-title)]">{item.title}</p>
        <EntityPickerCombobox
          onSelect={handleSelect}
          selectedId={selected?._id}
          disabled={attaching}
          autoFocus
        />
        {isDuplicate && (
          <p className="text-body-s text-[var(--text-badge-red)]">
            {t('trainingProgram.alreadyAdded')}
          </p>
        )}
      </div>
    </TeeFormModal>
  );
}
